import React, { useCallback, useEffect, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { usePermissions } from "../../hooks/usePermissions";
import { blockDemoSave } from "../../helpers/guestMode";
import { formatSurfaceTotals } from "../../helpers/measurementModel";
import {
  addProjectMeasurement,
  archiveProjectFeature,
  archiveProjectMeasurement,
  loadProjectStructure,
  formatTargetCompletion,
  updateProjectFeature,
  updateProjectMeasurement,
} from "../../helpers/projectStructure";
import { safeUserErrorMessage } from "../../helpers/safeErrors";
import { MeasurementFormSheet } from "../estimate/MeasurementFormSheet";
import {
  WorkspaceHeaderMeta,
  WorkspaceMeasurementSection,
} from "../workspace/WorkspaceChrome";
import { WorkspaceNameSheet } from "../workspace/WorkspaceNameSheet";

function toEditorSection(measurement) {
  return {
    ...measurement,
    section_id: measurement.project_measurement_id,
  };
}

/**
 * Project feature drill-down. Measurements use the shared sheet and row.
 * Planned materials, labour pricing, and pictures are not shown here.
 * Pictures can attach to the project or location later, not this feature.
 */
export function ProjectFeatureWorkspace({
  projectId,
  locationId,
  featureId,
  locationName,
  projectName = "",
  editable,
  onBack,
  featureRecord,
}) {
  const provided = featureRecord !== undefined;
  const { actor } = usePermissions();
  const [feature, setFeature] = useState(featureRecord);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRename, setShowRename] = useState(false);

  const reload = useCallback(async () => {
    if (provided) return;
    setError("");
    try {
      const tree = await loadProjectStructure(actor, projectId);
      const location = tree.find((row) => row.project_location_id === locationId);
      setFeature(
        (location?.features || []).find((row) => row.project_feature_id === featureId) || null,
      );
    } catch (err) {
      setError(safeUserErrorMessage(err, "Unable to load this feature."));
    }
  }, [actor, featureId, locationId, projectId, provided]);

  useEffect(() => {
    if (provided) {
      setFeature(featureRecord);
      return;
    }
    reload();
  }, [provided, featureRecord, reload]);

  const sections = (feature?.measurements || []).map(toEditorSection);
  const surfaceTotals = formatSurfaceTotals(feature?.surface_totals);
  const netSqft = sections.reduce((sum, row) => sum + Number(row.area_sqft || 0), 0);

  async function handleDeleteFeature() {
    if (!editable || !feature) return;
    if (!window.confirm(`Delete feature "${feature.name}"?`)) return;
    if (blockDemoSave(actor)) return;
    try {
      await archiveProjectFeature(actor, feature.project_feature_id);
      onBack?.();
    } catch (err) {
      setError(safeUserErrorMessage(err, "Unable to delete feature."));
    }
  }

  async function handleDeleteMeasurement(section) {
    if (!editable) return;
    if (!window.confirm(`Delete measurement "${section.name}"?`)) return;
    if (blockDemoSave(actor)) return;
    try {
      await archiveProjectMeasurement(actor, section.project_measurement_id);
      await reload();
    } catch (err) {
      setError(safeUserErrorMessage(err, "Unable to delete measurement."));
    }
  }

  return (
    <CrewPilotModuleShell
      title={feature?.name || "Feature"}
      subtitle={locationName || ""}
      sectionLabel="Feature"
      onBack={onBack}
      desktopBackLabel={locationName || "Location"}
      crumbs={["Projects", projectName, locationName]}
    >
      {error && <div className="est-inline-error">{error}</div>}

      {feature && (
        <>
          <WorkspaceHeaderMeta
            menuLabel="Feature actions"
            menuItems={
              editable
                ? [
                    { label: "Edit Feature", onClick: () => setShowRename(true) },
                    {
                      label: "Delete Feature",
                      onClick: handleDeleteFeature,
                      destructive: true,
                    },
                  ]
                : []
            }
          >
            {netSqft > 0 ? <span>{netSqft.toFixed(0)} ft²</span> : <span>Measurements</span>}
            {feature.target_completion_date ? (
              <span className="ws-target-meta">
                {" · Target "}
                {formatTargetCompletion(feature.target_completion_date)}
              </span>
            ) : null}
          </WorkspaceHeaderMeta>

          <WorkspaceMeasurementSection
            sections={sections}
            summary={
              surfaceTotals.length > 0
                ? surfaceTotals
                    .map((row) => `${row.label} ${Number(row.sqft).toFixed(1)} ft²`)
                    .join(" · ")
                : ""
            }
            editable={editable}
            onAdd={() => setShowAdd(true)}
            onEdit={setEditing}
            onDelete={handleDeleteMeasurement}
          />
        </>
      )}

      <div className="est-bottom-spacer" />

      {(showAdd || editing) && (
        <MeasurementFormSheet
          section={editing}
          existingSections={sections}
          onClose={() => {
            setShowAdd(false);
            setEditing(null);
          }}
          onSaved={reload}
          onPersist={async (form) => {
            if (blockDemoSave(actor)) {
              throw new Error("Demo Mode — Sign in to save your work.");
            }
            if (editing) {
              await updateProjectMeasurement(actor, editing.project_measurement_id, form);
            } else {
              await addProjectMeasurement(actor, featureId, form);
            }
          }}
        />
      )}

      {showRename && feature && (
        <WorkspaceNameSheet
          title="Edit Feature"
          fieldLabel="Feature Name"
          placeholder="Shower"
          initialName={feature.name}
          withTargetCompletion
          initialTargetCompletion={feature.target_completion_date || ""}
          primaryLabel="Save Changes"
          onClose={() => setShowRename(false)}
          onSubmit={async (name, options) => {
            if (blockDemoSave(actor)) {
              throw new Error("Demo Mode — Sign in to save your work.");
            }
            await updateProjectFeature(actor, feature.project_feature_id, name, options);
            await reload();
          }}
        />
      )}
    </CrewPilotModuleShell>
  );
}
